import { HFlow, Icon, PagedTable, Tag, Text, Tooltip } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import { TableBox } from 'components/table/TableBox'
import React from 'react'
import { useRouteMatch } from 'react-router'

import { Ellipsis } from '../../../components/Ellipsis'
import { usePagedTableProps } from '../../../components/table'
import {
  useAtividadeColetivaGrupoTableQuery,
  useExcluirGrupoParticipanteAtividadeColetivaMutation,
} from '../../../graphql/hooks.generated'
import { GrupoParticipanteAtividadeColetivaQueryInput } from '../../../graphql/types.generated'
import { convertQueryDataToTableData } from '../grupos/converter-atividadeColetivaGrupos'
import {
  AtividadeColetivaGruposFilterFormModel,
  AtividadeColetivaGruposTableModel,
} from '../grupos/model-atividadeColetivaGrupos'
import { MSG_EXCLUSAO_GRUPO_AC_MODAL } from '../grupos/utils-atividadeColetivaGrupos'
import { AtividadeColetivaGruposTableDropdownMenu } from './AtividadeColetivaGruposTableDropdownMenu'

interface AtividadeColetivaGruposTableProps {
  filter: GrupoParticipanteAtividadeColetivaQueryInput
  onChangeFilter: (filter: AtividadeColetivaGruposFilterFormModel) => void
}

export const AtividadeColetivaGruposTable = (props: AtividadeColetivaGruposTableProps) => {
  const { filter, onChangeFilter } = props
  const match = useRouteMatch()
  const handleRejection = useErrorHandler()
  const [excluirGrupoParticipanteAtividadeColetiva] = useExcluirGrupoParticipanteAtividadeColetivaMutation()

  const { data, loading, refetch } = useAtividadeColetivaGrupoTableQuery({
    variables: { input: filter },
    fetchPolicy: 'cache-and-network',
  })

  const queryContent = data?.gruposParticipantesAtividadeColetiva?.content
  const convertedQueryPayload = queryContent?.map((grupo) => convertQueryDataToTableData(grupo)) || []
  const tableData = data?.gruposParticipantesAtividadeColetiva && {
    content: convertedQueryPayload,
    pageInfo: data.gruposParticipantesAtividadeColetiva.pageInfo,
  }

  const tableProps = usePagedTableProps({
    loading: loading,
    result: tableData,
    onChange: onChangeFilter,
  })

  const handleExcluir = (grupoParticipanteAtividadeColetivaId: ID) => {
    confirm({
      title: 'Deseja excluir o grupo?',
      confirmLabel: 'Excluir',
      type: 'danger',
      body: MSG_EXCLUSAO_GRUPO_AC_MODAL,
      onConfirm: () => {
        excluirGrupoParticipanteAtividadeColetiva({ variables: { grupoParticipanteAtividadeColetivaId } })
          .then(() => {
            refetch({ input: filter })
          })
          .catch(handleRejection)
      },
    })()
  }

  const renderActions = (item: AtividadeColetivaGruposTableModel) => (
    <HFlow hSpacing={0} justifyContent='flex-end'>
      <Tooltip text='Visualizar'>
        <ButtonLink size='small' skin='ghost' to={`${match.url}/visualizar/${item.id}`}>
          <Icon icon='zoomOutline' />
        </ButtonLink>
      </Tooltip>
      <AtividadeColetivaGruposTableDropdownMenu onExcluir={() => handleExcluir(item.id)} />
    </HFlow>
  )

  return (
    <TableBox>
      <PagedTable<AtividadeColetivaGruposTableModel>
        loading={loading}
        columns={[
          {
            name: 'nomeGrupo',
            header: 'Nome do grupo',
            render: (row) => <Text>{row.nome}</Text>,
          },
          {
            name: 'tipoGrupo',
            header: 'Tipo de grupo',
            render: (row) => <Text>{row.tipoGrupo}</Text>,
          },
          {
            name: 'numeroParticipantes',
            header: 'Nº de participantes',
            render: (row) => <Text>{row.quantidadeCidadaosParticipantes}</Text>,
          },
          {
            name: 'unidadeSaude',
            header: 'Unidade de saúde',
            render: (row) => <Text>{row.unidadeSaude}</Text>,
          },
          {
            name: 'equipesEnvolvidas',
            header: 'Equipes envolvidas',
            render: (row) => <Ellipsis maxLines={2}>{row.equipesEnvolvidas}</Ellipsis>,
            style: { maxWidth: '16rem' },
          },
          {
            name: 'isAtivo',
            render: (row) => (row.ativo ? '' : <Tag type='normal'>Inativo</Tag>),
          },
          {
            name: 'actions',
            align: 'right',
            render: renderActions,
          },
        ]}
        {...tableProps}
      />
    </TableBox>
  )
}
